import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { CONSENT_SECTION_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';

// Parse description — may be a JSON string (raw API) or a plain text string (transformed)
function parseDescription(desc) {
    if (!desc) return { text: '', sectionType: null, style: null };
    // If it looks like JSON (starts with {), try to parse
    if (typeof desc === 'string' && desc.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(desc);
            return {
                text: parsed.text || '',
                sectionType: parsed.frontend_type || null,
                style: parsed.style || null,
            };
        } catch {
            // Not valid JSON, treat as plain text
        }
    }
    return { text: desc, sectionType: null, style: null };
}

const HIDDEN_NAME_TYPES = ['brand_image', 'divider', 'paragraph'];

// Section type → accent color + icon for visual differentiation
const SECTION_ACCENTS = {
    patient_info: { color: '#078DEE', icon: null, label: 'Patient Information' },
    medical_history: { color: '#FF5630', icon: null, label: 'Medical History' },
    risks_benefits: { color: '#FFAB00', icon: null, label: 'Risks & Benefits' },
    consent: { color: '#00AB55', icon: null, label: 'Consent' },
    declaration: { color: '#00AB55', icon: null, label: 'Declaration' },
    signature: { color: '#7635DC', icon: null, label: 'Signature' },
    custom: { color: '#637381', icon: null, label: null },
};

function getSectionAccent(sectionType, sectionName) {
    if (SECTION_ACCENTS[sectionType] && sectionType !== 'custom') {
        return SECTION_ACCENTS[sectionType];
    }
    // Infer from section name keywords
    const nameLower = (sectionName || '').toLowerCase();
    if (nameLower.includes('signature') || nameLower.includes('sign')) return SECTION_ACCENTS.signature;
    if (nameLower.includes('consent') || nameLower.includes('declaration')) return SECTION_ACCENTS.consent;
    if (nameLower.includes('risk') || nameLower.includes('benefit')) return SECTION_ACCENTS.risks_benefits;
    if (nameLower.includes('medical') || nameLower.includes('health') || nameLower.includes('history')) return SECTION_ACCENTS.medical_history;
    if (nameLower.includes('detail') || nameLower.includes('personal') || nameLower.includes('patient') || nameLower.includes('your detail') || nameLower.includes('emergency') || nameLower.includes('contact')) return SECTION_ACCENTS.patient_info;
    if (nameLower.includes('treatment') || nameLower.includes('procedure')) return { color: '#078DEE', icon: '🦷', label: null };
    return SECTION_ACCENTS.custom;
}

const ConsentSectionWrapper = ({ section, children, theme }) => {
    const { section_type: rawSectionType, name, description, style: sectionStyle } = section;

    // Resolve section type and description text
    const { text: descText, sectionType: parsedType, style: descStyle } = useMemo(
        () => parseDescription(description),
        [description]
    );

    // Use explicit section_type if set, otherwise fall back to parsed from description JSON
    const sectionType = rawSectionType || parsedType || 'custom';

    // Merge styles: explicit section.style > description meta style > theme > defaults
    const mergedStyle = sectionStyle || descStyle || {};

    if (sectionType === CONSENT_SECTION_TYPES.BRAND_IMAGE) {
        return <Box sx={{ textAlign: 'center', py: 2 }}>{children}</Box>;
    }

    const showName = name && !HIDDEN_NAME_TYPES.includes(sectionType);
    const accent = getSectionAccent(sectionType, name);

    // Layout mode drives whether sections look like cards or flow seamlessly
    const layoutMode = theme?.layoutMode || 'card';
    const isFlat = layoutMode === 'flat' || layoutMode === 'minimal';

    const bgColor = isFlat
        ? (mergedStyle.backgroundColor || 'transparent')
        : (mergedStyle.backgroundColor || theme?.sectionBackgroundColor || '#ffffff');
    const borderColor = isFlat
        ? 'transparent'
        : (mergedStyle.borderColor || theme?.sectionBorderColor || 'rgba(145,158,171,0.12)');
    const borderWidth = isFlat
        ? 0
        : (mergedStyle.borderWidth ?? theme?.sectionBorderWidth ?? 1);
    const borderRadius = isFlat
        ? 0
        : (mergedStyle.borderRadius ?? theme?.sectionBorderRadius ?? 16);
    const padding = mergedStyle.padding ?? theme?.sectionPadding ?? 28;
    const marginTop = mergedStyle.marginTop ?? 0;
    const marginBottom = mergedStyle.marginBottom ?? (theme?.sectionSpacing ?? 20);

    // Refined card shadow — soft outer glow for depth without heaviness
    const sectionShadow = !isFlat && (bgColor !== 'transparent' || borderWidth > 0 || borderRadius > 0)
        ? '0 1px 2px rgba(0,0,0,0.03), 0 2px 8px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.03)'
        : 'none';

    return (
        <Box
            sx={{
                bgcolor: bgColor,
                border: { xs: 'none', sm: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none' },
                borderRadius: { xs: 0, sm: `${borderRadius}px` },
                px: { xs: 0, sm: `${padding}px` },
                py: `${padding}px`,
                mt: marginTop ? `${marginTop}px` : 0,
                mb: `${marginBottom}px`,
                boxShadow: { xs: 'none', sm: sectionShadow },
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                // Subtle left accent bar — refined 3px bar with rounded cap
                ...(!isFlat && accent.color !== '#637381' && {
                    '&::before': {
                        content: '""',
                        display: { xs: 'none', sm: 'block' },
                        position: 'absolute',
                        left: 0,
                        top: 12,
                        bottom: 12,
                        width: '3px',
                        bgcolor: accent.color,
                        borderRadius: '0 3px 3px 0',
                        opacity: 0.45,
                    },
                }),
            }}
        >
            {showName && (
                <>
                    {/* Divider above heading */}
                    {mergedStyle.headingDividerAbove && (
                        <Box sx={{
                            borderTop: `${mergedStyle.headingDividerAboveWidth || 1}px solid ${mergedStyle.headingDividerAboveColor || 'rgba(145,158,171,0.24)'}`,
                            mb: `${mergedStyle.headingDividerAboveSpacing ?? 12}px`,
                        }} />
                    )}
                    {/* Section heading with optional accent icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: mergedStyle.headingMarginBottom != null ? `${mergedStyle.headingMarginBottom}px` : 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: mergedStyle.headingFontWeight ?? theme?.headingFontWeight ?? 700,
                                color: mergedStyle.headingColor || theme?.headingColor || '#1A2027',
                                fontSize: `${mergedStyle.headingFontSize || theme?.headingFontSize || 17}px`,
                                fontFamily: mergedStyle.headingFontFamily || theme?.headingFontFamily || theme?.fontFamily || 'inherit',
                                textAlign: mergedStyle.headingAlignment || theme?.headingAlignment || 'left',
                                fontStyle: mergedStyle.headingFontStyle || 'normal',
                                textTransform: mergedStyle.headingTextTransform || theme?.headingTextTransform || 'none',
                                textDecoration: mergedStyle.headingTextDecoration || 'none',
                                lineHeight: mergedStyle.headingLineHeight || 1.4,
                                letterSpacing: `${mergedStyle.headingLetterSpacing ?? theme?.headingLetterSpacing ?? -0.02}px`,
                                width: '100%',
                            }}
                        >
                            {name}
                        </Typography>
                    </Box>
                    {/* Divider below heading — check section style then theme fallback */}
                    {(mergedStyle.headingDividerBelow ?? theme?.headingDividerBelow) && (
                        <Box sx={{
                            borderTop: `${mergedStyle.headingDividerBelowWidth || theme?.headingDividerBelowWidth || 1}px solid ${mergedStyle.headingDividerBelowColor || theme?.headingDividerBelowColor || 'rgba(145,158,171,0.24)'}`,
                            mb: `${mergedStyle.headingDividerBelowSpacing ?? theme?.headingDividerBelowSpacing ?? 12}px`,
                        }} />
                    )}
                </>
            )}
            {descText && (
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2.5,
                        color: theme?.typographyBody2Color || theme?.bodyColor || '#919EAB',
                        fontSize: theme?.typographyBody2 ? `${theme.typographyBody2}px` : (theme?.bodyFontSize || '13px'),
                        fontFamily: theme?.fontFamily || 'inherit',
                        lineHeight: 1.7,
                    }}
                >
                    {descText}
                </Typography>
            )}
            {children}
        </Box>
    );
};

ConsentSectionWrapper.propTypes = {
    section: PropTypes.object.isRequired,
    children: PropTypes.node,
    theme: PropTypes.object,
};

ConsentSectionWrapper.defaultProps = {
    children: null,
    theme: null,
};

export default ConsentSectionWrapper;
